/**
 * Hooks for user-specific Supabase data.
 * These all require the user's ID from useAuth().
 * Data is empty until the user is signed in via Supabase Auth.
 */
import { useQuery } from '@tanstack/react-query';
import {
  getWalletTransactions,
  getNotifications,
  getBuyerPurchases,
  getSellerProducts,
  getSellerSales,
  getProfile,
} from '../lib/queries';

export function useProfile(userId: string | undefined) {
  return useQuery({
    queryKey: ['profile', userId],
    queryFn: () => getProfile(userId!),
    enabled: !!userId,
  });
}

export function useWalletTransactions(userId: string | undefined) {
  return useQuery({
    queryKey: ['wallet', userId],
    queryFn: async () => {
      const { data, error } = await getWalletTransactions(userId!);
      if (error) throw error;
      return data;
    },
    enabled: !!userId,
  });
}

export function useNotifications(userId: string | undefined) {
  return useQuery({
    queryKey: ['notifications', userId],
    queryFn: async () => {
      const { data, error } = await getNotifications(userId!);
      if (error) throw error;
      return data;
    },
    enabled: !!userId,
  });
}

export function usePurchases(userId: string | undefined) {
  return useQuery({
    queryKey: ['purchases', 'buyer', userId],
    queryFn: async () => {
      const { data, error } = await getBuyerPurchases(userId!);
      if (error) throw error;
      return data;
    },
    enabled: !!userId,
  });
}

export function useMyProducts(sellerId: string | undefined) {
  return useQuery({
    queryKey: ['products', 'seller', sellerId],
    queryFn: async () => {
      const { data, error } = await getSellerProducts(sellerId!);
      if (error) throw error;
      return data;
    },
    enabled: !!sellerId,
  });
}

export function useSales(sellerId: string | undefined) {
  return useQuery({
    queryKey: ['sales', sellerId],
    queryFn: async () => {
      const { data, error } = await getSellerSales(sellerId!);
      if (error) throw error;
      return data;
    },
    enabled: !!sellerId,
  });
}
